import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode, Ref } from 'react';

export { clsx as cn };

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'enforce';
export type ButtonSize = 'sm' | 'md';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  // Black pill: the one primary action per view.
  primary: 'rounded-full bg-primary text-on-primary hover:bg-primary-hover border border-transparent',
  secondary: 'rounded-full bg-surface text-ink border border-line-strong hover:shadow-float',
  ghost: 'rounded-md bg-transparent text-ink-2 border border-transparent hover:bg-surface-2 hover:text-ink',
  danger: 'rounded-full bg-surface text-bad border border-line-strong hover:bg-bad-soft',
  // The enforcement accent (Notion blue): only for decisions that release or block governed effects.
  enforce:
    'rounded-full bg-accent text-on-accent hover:bg-accent-hover border border-transparent font-semibold',
};

// Hit areas: 36px minimum on desktop (`sm`), 40px for `md` (primary and enforcement actions), 44px for coarse
// pointers via `.wb-btn` in tokens.css. Sizes are in px: the root is 14px, so `h-9` would only be 31.5px.
const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-[36px] px-3.5 text-[13px] gap-1.5',
  md: 'h-[40px] px-4 text-sm gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

const BUTTON_BASE =
  'wb-btn inline-flex select-none items-center justify-center whitespace-nowrap font-medium transition-[color,background-color,border-color,box-shadow] duration-150';

/** Class names of a `Button`, for links that should look like one (e.g. "Review 2 proposed actions"). */
export function buttonClass(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  className?: string,
): string {
  return clsx(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className);
}

/** Button with a clear hierarchy: `primary` once per view, `enforce` only at enforcement points. */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={clsx(
        BUTTON_BASE,
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="size-3.5 animate-spin" /> : icon}
      {children}
    </button>
  );
}

export type IconButtonSize = 'sm' | 'md';

// 36px minimum (`sm`), 40px `md`; 44px for coarse pointers via `.wb-icon-btn` in tokens.css.
const ICON_BUTTON_SIZES: Record<IconButtonSize, string> = {
  sm: 'size-[36px] [&_svg]:size-3.5',
  md: 'size-[40px] [&_svg]:size-4',
};

/**
 * Class names of the circular icon control, for links and other elements that should look like an
 * `IconButton` (navigation rail, row actions).
 */
export function iconButtonClass(size: IconButtonSize = 'md', className?: string): string {
  return clsx(
    'wb-icon-btn inline-flex shrink-0 select-none items-center justify-center rounded-full border border-line bg-surface text-ink-2 transition-colors duration-150 hover:bg-surface-2 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50',
    ICON_BUTTON_SIZES[size],
    className,
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: the button has no visible text. */
  'aria-label': string;
  size?: IconButtonSize;
  ref?: Ref<HTMLButtonElement>;
}

/** Circular icon-only button (40px, `sm` 36px): white surface, hairline, ink-2 glyph. */
export function IconButton({ size = 'md', className, type = 'button', ...rest }: IconButtonProps) {
  return <button type={type} className={iconButtonClass(size, className)} {...rest} />;
}

export type Tone = 'neutral' | 'info' | 'ok' | 'warn' | 'bad' | 'accent';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-2 border-line',
  info: 'bg-info-soft text-info-ink border-transparent',
  ok: 'bg-ok-soft text-ok-ink border-transparent',
  warn: 'bg-warn-soft text-warn-ink border-transparent',
  bad: 'bg-bad-soft text-bad-ink border-transparent',
  accent: 'bg-accent-soft text-accent-ink border-transparent',
};

export const DOT_TONES: Record<Tone, string> = {
  neutral: 'bg-ink-3',
  info: 'bg-info',
  ok: 'bg-ok',
  warn: 'bg-warn',
  bad: 'bg-bad',
  accent: 'bg-accent',
};

/**
 * Status hue as `currentColor`, for stroke icons (the task detail glyphs). Same tokens as `DOT_TONES`: the solid
 * status colour, gray `ink-3` for neutral. Pair it with a `TEXT_TONES` word; the icon never carries meaning alone.
 */
export const ICON_TONES: Record<Tone, string> = {
  neutral: 'text-ink-3',
  info: 'text-info',
  ok: 'text-ok',
  warn: 'text-warn',
  bad: 'text-bad',
  accent: 'text-accent',
};

/**
 * Coloured text without a fill, for statuses in dense tables and lists (the dot carries the shape cue). The `-ink`
 * variants keep WCAG AA (4.5:1) on surfaces and soft tints in both themes.
 */
export const TEXT_TONES: Record<Tone, string> = {
  neutral: 'text-ink-2',
  info: 'text-info-ink',
  ok: 'text-ok-ink',
  warn: 'text-warn-ink',
  bad: 'text-bad-ink',
  accent: 'text-accent-ink',
};

/** `pill`: soft-tinted pill (default). `text`: dot plus coloured text, no fill, for table cells and lists. */
export type BadgeVariant = 'pill' | 'text';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  mono?: boolean;
  dot?: boolean;
  pulse?: boolean;
  variant?: BadgeVariant;
}

/** Compact status label. Use `mono` for codes (`A3`, `irreversible_write`) and `variant="text"` in tables. */
export function Badge({
  tone = 'neutral',
  mono,
  dot,
  pulse,
  variant = 'pill',
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={clsx(
        // `relative` keeps sr-only children (raw values next to friendly labels) inside scroll containers.
        'wb-display relative inline-flex shrink-0 items-center whitespace-nowrap font-medium leading-none',
        variant === 'pill'
          ? ['h-5.5 gap-1.5 rounded-full border px-2 text-[12px]', TONES[tone]]
          : ['min-h-5.5 gap-2 text-[13.5px]', TEXT_TONES[tone]],
        mono && 'font-mono text-[12px] tracking-tight',
        className,
      )}
      {...rest}
    >
      {dot && (
        <span
          aria-hidden
          className={clsx(
            variant === 'pill' ? 'size-1.5 rounded-full' : 'size-[7px] rounded-full',
            DOT_TONES[tone],
            pulse && 'animate-[wb-pulse_1.6s_ease-in-out_infinite]',
          )}
        />
      )}
      {children}
    </span>
  );
}

/** Inline monospace text for ids, hashes, tool names. */
export function Mono({ className, ...rest }: HTMLAttributes<HTMLElement>) {
  return <code className={clsx('font-mono text-[12.5px] tracking-tight text-ink', className)} {...rest} />;
}

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

const CARD_PADDING: Record<CardPadding, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-5 md:p-6',
  lg: 'p-6 md:p-7',
};

export interface CardOptions {
  /** Inner padding; `none` for flush content (tables) that brings its own. */
  padding?: CardPadding;
  /** `muted` sits a tile on the canvas tint instead of white (quiet side panels, onboarding blurbs). */
  surface?: 'white' | 'muted';
  /** Ring the card in a tone (the decision card uses `accent`, the enforcement colour). */
  highlight?: Tone;
  className?: string;
}

const HIGHLIGHT_RINGS: Record<Tone, string> = {
  neutral: 'ring-[1.5px] ring-line-strong',
  info: 'ring-[1.5px] ring-info',
  ok: 'ring-[1.5px] ring-ok',
  warn: 'ring-[1.5px] ring-warn',
  bad: 'ring-[1.5px] ring-bad',
  accent: 'ring-[1.5px] ring-accent',
};

/**
 * Class names of a bento card (`.wb-card` in tokens.css: 20px radius, no border, soft ring and lift). Use it on any
 * element that should read as one region on the canvas; layout and spacing stay utilities on top.
 */
export function cardClass({
  padding = 'none',
  surface = 'white',
  highlight,
  className,
}: CardOptions = {}): string {
  return clsx(
    'wb-card min-w-0',
    surface === 'muted' && 'bg-surface-2 shadow-none',
    highlight && HIGHLIGHT_RINGS[highlight],
    CARD_PADDING[padding],
    className,
  );
}

type CardElement = 'div' | 'section' | 'article' | 'aside' | 'header' | 'form' | 'nav';

export interface CardProps extends HTMLAttributes<HTMLElement>, Omit<CardOptions, 'className'> {
  /** Element to render; pick the landmark that fits (`section` with a heading, `aside`, `form`). */
  as?: CardElement;
}

/** A bento card. Do not nest cards: inside one, use `Section plain` groups or muted 12px tiles. */
export function Card({ as: Tag = 'div', padding = 'md', surface, highlight, className, ...rest }: CardProps) {
  return (
    <Tag
      className={cardClass({
        padding,
        ...(surface ? { surface } : {}),
        ...(highlight ? { highlight } : {}),
        ...(className ? { className } : {}),
      })}
      {...rest}
    />
  );
}

export interface SectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  /** Small caption above the title. */
  eyebrow?: ReactNode;
  actions?: ReactNode;
  /** Flush content (tables) without inner padding. */
  flush?: boolean;
  headingLevel?: 2 | 3;
  /**
   * A titled group inside another card: no frame and no padding (flush content gets a 12px nested tile).
   * Use it instead of nesting cards.
   */
  plain?: boolean;
}

/** A bento card (`cardClass`) with an optional header row. Do not nest cards: use `plain`. */
export function Section({
  title,
  eyebrow,
  actions,
  flush,
  headingLevel = 2,
  plain = false,
  className,
  children,
  ...rest
}: SectionProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const hasHeader = !!(title || actions);
  const bodyClass = plain
    ? flush && 'overflow-hidden rounded-xl border border-line'
    : !flush && (hasHeader ? 'px-6 pt-4 pb-6 md:px-7 md:pb-7' : 'p-6 md:p-7');
  return (
    <section
      className={clsx(plain ? 'min-w-0' : cardClass(), !plain && flush && 'overflow-hidden', className)}
      {...rest}
    >
      {hasHeader && (
        <header
          className={clsx(
            'flex flex-col items-start justify-between gap-3 sm:flex-row sm:flex-wrap',
            plain ? 'pb-3' : 'px-6 pt-6 md:px-7',
            !plain && flush && 'pb-4',
          )}
        >
          <div className="min-w-0">
            {eyebrow && <div className="mb-0.5 text-[13px] text-ink-3">{eyebrow}</div>}
            {title && (
              <Heading
                className={clsx(
                  'leading-snug font-medium tracking-[-0.01em] text-ink [overflow-wrap:anywhere]',
                  // Heading scale 20/24/28 (F18): card headings are 20px; nested h3 sub-headings stay 16px.
                  headingLevel === 2 && !plain ? 'text-[20px]' : 'text-[16px]',
                )}
              >
                {title}
              </Heading>
            )}
          </div>
          {actions && <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={clsx(bodyClass)}>{children}</div>
    </section>
  );
}

/** Small caption used for field groups and table captions (sentence case). */
export function Eyebrow({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx('wb-display text-[13px] font-medium text-ink-3', className)} {...rest} />;
}

export interface KeyValueProps {
  items: { label: ReactNode; value: ReactNode; key?: string }[];
  columns?: 1 | 2;
  className?: string;
}

/** Definition list in a dense two-column grid. */
export function KeyValue({ items, columns = 1, className }: KeyValueProps) {
  return (
    <dl
      className={clsx(
        'grid gap-x-4 gap-y-2.5',
        columns === 2
          ? 'grid-cols-[minmax(7rem,max-content)_minmax(0,1fr)] sm:grid-cols-[max-content_minmax(0,1fr)_max-content_minmax(0,1fr)]'
          : 'grid-cols-[minmax(7rem,max-content)_minmax(0,1fr)]',
        className,
      )}
    >
      {items.map((item, i) => (
        <div key={item.key ?? i} className="contents">
          <dt className="text-[13px] text-ink-3">{item.label}</dt>
          <dd className="min-w-0 break-words text-[13.5px] text-ink">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface EmptyStateProps {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}

/** Empty list state that says what would appear here and how to get it. */
export function EmptyState({ title, children, action, icon, className }: EmptyStateProps) {
  return (
    <div className={clsx('flex flex-col items-center gap-2 px-6 py-10 text-center', className)}>
      {icon && <div className="text-ink-3">{icon}</div>}
      <p className="text-[15px] font-semibold tracking-[-0.01em] text-ink">{title}</p>
      {children && <div className="wb-prose text-ink-2">{children}</div>}
      {action && <div className="pt-2">{action}</div>}
    </div>
  );
}

/** Spinner with an accessible label. */
export function Spinner({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <span role="status" className={clsx('inline-flex items-center gap-2 text-[13px] text-ink-3', className)}>
      <Loader2 aria-hidden className="size-4 animate-spin" />
      <span>{label}…</span>
    </span>
  );
}

/** Placeholder rows while a list loads. */
export function SkeletonRows({ rows = 5, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-label={label} className="divide-y divide-line">
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
        <div key={i} className="flex items-center gap-4 px-4 py-3.5">
          <div className="h-3 w-24 animate-[wb-pulse_1.6s_ease-in-out_infinite] rounded-full bg-surface-3" />
          <div className="h-3 flex-1 animate-[wb-pulse_1.6s_ease-in-out_infinite] rounded-full bg-surface-3" />
          <div className="h-3 w-16 animate-[wb-pulse_1.6s_ease-in-out_infinite] rounded-full bg-surface-3" />
        </div>
      ))}
    </div>
  );
}

export interface BannerProps {
  tone?: Tone;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  role?: 'status' | 'alert';
}

/** Full-width message strip (restart required, kill switch engaged, errors): a soft-tinted tile. */
export function Banner({ tone = 'info', title, children, action, className, role = 'status' }: BannerProps) {
  return (
    <div
      role={role}
      className={clsx(
        'flex items-start justify-between gap-4 rounded-xl border px-4 py-3',
        TONES[tone],
        className,
      )}
    >
      <div className="min-w-0">
        <div className="text-[14px] font-semibold">{title}</div>
        {children && <div className="mt-0.5 max-w-[75ch] text-[14px] leading-[1.5]">{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export interface StatTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  /** Shown as a 6px dot before the label; the number itself stays ink. */
  tone?: Tone;
  /** `soft` (default) for tiles inside a card, `white` for a bento card directly on the canvas. */
  surface?: 'soft' | 'white';
  /** `lg` (default) for KPI rows, `md` for figures and amounts that run longer. */
  size?: 'md' | 'lg';
}

/** A big-number tile (bento KPI): label on top, a light display numeral, an optional hint below. */
export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  surface = 'soft',
  size = 'lg',
}: StatTileProps) {
  return (
    <div
      className={clsx(
        'flex min-h-28 min-w-0 flex-col gap-3 sm:min-h-36 sm:gap-4',
        surface === 'white'
          ? cardClass({ className: 'p-4 sm:px-6 sm:py-5.5' })
          : 'rounded-xl bg-surface-2 p-4 sm:p-5',
      )}
    >
      <div className="flex items-center gap-1.5 text-[13.5px] text-ink-3">
        {tone !== 'neutral' && (
          <span aria-hidden className={clsx('size-1.5 shrink-0 rounded-full', DOT_TONES[tone])} />
        )}
        <span className="min-w-0">{label}</span>
      </div>
      <div
        className={clsx(
          'wb-num truncate text-ink',
          size === 'lg'
            ? 'text-[36px] sm:text-[44px] md:text-[52px]'
            : 'text-[26px] sm:text-[32px] md:text-[38px]',
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-auto text-[12.5px] text-ink-3">{hint}</div>}
    </div>
  );
}

/** Responsive row of `StatTile`s (or cards): two columns on phones, then as many 200px-plus columns as fit. */
export function StatGrid({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        'grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] sm:gap-4',
        className,
      )}
      {...rest}
    />
  );
}

/**
 * Class names of one segment in a `Segmented` group. The current segment is styled from `aria-current="page"`,
 * `aria-pressed="true"` or TanStack Router's `data-status="active"`, so links and toggle buttons both work.
 */
export function segmentClass(className?: string): string {
  return clsx(
    'wb-display inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[13.5px] font-medium text-ink-3 transition-[color,background-color,box-shadow] duration-150 hover:text-ink',
    'aria-[current=page]:bg-surface aria-[current=page]:text-ink aria-[current=page]:shadow-card',
    'aria-pressed:bg-surface aria-pressed:text-ink aria-pressed:shadow-card',
    'data-[status=active]:bg-surface data-[status=active]:text-ink data-[status=active]:shadow-card',
    className,
  );
}

/** Pill track that holds `segmentClass` links or buttons (view presets, status filters). */
export function Segmented({ className, ...rest }: HTMLAttributes<HTMLElement> & { 'aria-label': string }) {
  return (
    <nav
      className={clsx(
        // One row that scrolls sideways on narrow screens rather than wrapping into a lumpy block.
        'inline-flex max-w-full gap-0.5 overflow-x-auto rounded-full bg-surface-2 p-1 [scrollbar-width:none] [&>*]:shrink-0',
        className,
      )}
      {...rest}
    />
  );
}

/** Visually hidden text for screen readers. */
export function VisuallyHidden({ children, ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className="sr-only" {...rest}>
      {children}
    </span>
  );
}

/**
 * Data table: a soft header band with rounded ends and hairline rows (`.wb-table`). Children are
 * `<thead>`/`<tbody>`. Put it in a flush card; the 8px inset keeps the band off the card edge.
 */
export function Table({
  className,
  scrollLabel,
  ...rest
}: HTMLAttributes<HTMLTableElement> & {
  /**
   * Added by 21d: names the horizontal scroll region and makes it keyboard focusable, for tables whose cells may have
   * no focusable content (axe `scrollable-region-focusable`).
   */
  scrollLabel?: string;
}) {
  return (
    <div
      className="w-full overflow-x-auto p-2"
      {...(scrollLabel ? { role: 'region', 'aria-label': scrollLabel, tabIndex: 0 } : {})}
    >
      <table className={clsx('wb-table w-full text-left text-[13.5px]', className)} {...rest} />
    </div>
  );
}
